import React, { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import type { BugReportContext } from "../utils/bugReport";
import {
    buildEmailBody,
    buildEmailSubject,
    generateMailtoLink,
    openMailtoLink,
} from "../utils/bugReport";
import { contactPageEnabled } from "../utils/featureFlags";

interface ReportBugButtonProps {
    pageName?: string;
    className?: string;
}

export const ReportBugButton: React.FC<ReportBugButtonProps> = ({
    pageName,
    className = "",
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const fallbackModalRef = useRef<HTMLDialogElement>(null);

    const getUserRole = (): string => {
        if (!user) return "Not logged in";
        if (user.is_organisation_admin) return "Admin";
        if (user.is_auditor) return "Auditor";
        return "User";
    };

    const handleReportBug = () => {
        // Determine page name from route if not provided
        const currentPageName = pageName || getPageNameFromPath(location.pathname);

        if (contactPageEnabled) {
            // Origin travels in router state, not the URL, so it can't be forged
            // by a crafted link into the support inbox.
            navigate("/contact?type=bug", {
                state: { from: window.location.href, fromName: currentPageName },
            });
            return;
        }

        // Build context
        const context: BugReportContext = {
            pageName: currentPageName,
            pageUrl: window.location.href,
            userRole: user ? getUserRole() : undefined,
            organisationName: undefined, // Can't reliably get this without additional prop
            labName: undefined, // Can't reliably get this without additional prop
        };

        // Generate mailto link
        const mailtoUrl = generateMailtoLink(context);

        // Try to open email client
        openMailtoLink(mailtoUrl);

        // Show fallback modal after a short delay to allow email client to open
        setTimeout(() => {
            fallbackModalRef.current?.showModal();
        }, 500);
    };

    const getPageNameFromPath = (path: string): string => {
        // Extract page name from URL path
        const cleanPath = path.replace(/^\//, "").replace(/\/$/, "");

        if (!cleanPath || cleanPath === "") return "Home";
        if (cleanPath === "dashboard") return "Dashboard";
        if (cleanPath.startsWith("dashboard/")) {
            const subPath = cleanPath.replace("dashboard/", "");
            if (subPath === "variables") return "Variables Input";
            if (subPath === "register-lab") return "Register Lab";
            if (subPath === "view-labs") return "View Labs";
            if (subPath === "impact-reports") return "Impact Reports";
        }
        if (cleanPath.startsWith("lab/")) return "Lab Details";
        if (cleanPath === "invitations") return "Manage Invitations";
        if (cleanPath === "login") return "Login";
        if (cleanPath === "register") return "Register";
        if (cleanPath === "about") return "About";
        if (cleanPath === "faq") return "FAQ";

        // Fallback: capitalize first letter
        return cleanPath.charAt(0).toUpperCase() + cleanPath.slice(1).replace(/-/g, " ");
    };

    const handleCopyToClipboard = () => {
        const currentPageName = pageName || getPageNameFromPath(location.pathname);
        const context: BugReportContext = {
            pageName: currentPageName,
            pageUrl: window.location.href,
            userRole: user ? getUserRole() : undefined,
        };

        const subject = buildEmailSubject(context.pageName);
        const body = buildEmailBody(context);
        const fullText = `To: support@labzero.eco\nSubject: ${subject}\n\n${body}`;

        navigator.clipboard.writeText(fullText).then(
            () => {
                alert("Email template copied to clipboard!");
            },
            (err) => {
                console.error("Failed to copy:", err);
                alert("Failed to copy to clipboard. Please select and copy manually.");
            }
        );
    };

    return (
        <>
            <button
                onClick={handleReportBug}
                className={`btn btn-sm bg-gray-200 hover:bg-gray-300 text-black border-gray-300 whitespace-nowrap ${className}`}
                type="button"
            >
                <span className="hidden sm:inline">Report bug / Give feedback</span>
                <span className="inline sm:hidden">Report bug</span>
            </button>

            {/* Fallback Modal */}
            <dialog ref={fallbackModalRef} className="modal">
                <div className="modal-box max-w-2xl">
                    <h3 className="font-bold text-lg mb-4">Report Bug / Give Feedback</h3>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <p className="text-sm">
                            <strong>If your email client didn't open:</strong>
                        </p>
                        <p className="text-sm mt-2">
                            Please email <strong>support@labzero.eco</strong> and copy the details below.
                        </p>
                    </div>

                    <div className="bg-base-200 rounded p-4 max-h-96 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap font-mono">
                            {`To: support@labzero.eco
Subject: ${buildEmailSubject(pageName || getPageNameFromPath(location.pathname))}

${buildEmailBody({
                                pageName: pageName || getPageNameFromPath(location.pathname),
                                pageUrl: window.location.href,
                                userRole: user ? getUserRole() : undefined,
                            })}`}
                        </pre>
                    </div>

                    <div className="modal-action">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleCopyToClipboard}
                        >
                            Copy to Clipboard
                        </button>
                        <form method="dialog">
                            <button className="btn btn-sm">Close</button>
                        </form>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    );
};
